import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import BaseListItem from '../BaseListItem';
import BaseListCell from '../BaseListItem/BaseListCell';
import DeviationThumb from '../../_partials/DeviationThumb';
import DeviationTitle from '../../_partials/DeviationTitle';
import DeviationPublishedTime from '../../_partials/DeviationPublishedTime';
import DeviationActions from '../../../tableActions/DeviationActions';
import StatItem from '../../_partials/StatItem';
import { StudioDeviationListContext } from '../../context';
import { OriginalsData } from '../../../../types/store';

import s from './OriginalListItem.scss';

interface Props {
  deviation: PapiDeviation;
  originalsData?: OriginalsData;
  offset: number;
  isUnlisted?: boolean;
}

const OriginalListItem: FC<Props> = ({
  deviation,
  originalsData,
  offset,
  isUnlisted,
}) => {
  const { t } = useTranslation();
  const { actionsColRef, actionsColWidth } = useContext(
    StudioDeviationListContext
  );
  // We use the first row as a probe to determine the size of columns
  const shouldAddColumnRefs = offset === 0;

  const originals = deviation.originals;
  const basePrice = originals?.basePrice;
  const dollarPrice = basePrice ? formatDollars(basePrice) : '---';

  const pendingCount = originalsData?.pendingOrdersCount ?? 0;
  const inProgressCount = originalsData?.inProgressOrdersCount ?? 0;
  const completedCount = originals?.completedOrdersCount ?? 0;

  return (
    <BaseListItem>
      <BaseListCell>
        <DeviationThumb deviation={deviation} width={100} height={64} />
      </BaseListCell>
      <BaseListCell className={s['cell-title']}>
        <DeviationTitle deviation={deviation} showSaleIndicators={false} />
        <div className={s['info']}>
          <DeviationPublishedTime deviation={deviation} />
          <div className={s['separator']} />
          <span className={s['starting-price']}>
            {t('studio.products.original.startingPrice', {
              price: dollarPrice,
            })}
          </span>
        </div>
      </BaseListCell>
      {!isUnlisted && (
        <>
          <BaseListCell className={s['pending-request-cell']}>
            <StatItem
              title={t('studio.products.original.stats.pendingRequest')}
              value={pendingCount}
            />
          </BaseListCell>
          <BaseListCell className={s['in-progress-cell']}>
            <StatItem
              title={t('studio.products.original.stats.inProgress')}
              value={inProgressCount}
            />
          </BaseListCell>
          <BaseListCell className={s['completed-cell']}>
            <StatItem
              title={t('studio.products.original.stats.completed')}
              value={completedCount}
            />
          </BaseListCell>
        </>
      )}
      <BaseListCell
        className={!isUnlisted ? s['actions-cell'] : undefined}
        ref={shouldAddColumnRefs ? actionsColRef : undefined}
        overflowHidden
      >
        <DeviationActions
          deviation={deviation}
          offset={offset}
          columnWidth={actionsColWidth}
        />
      </BaseListCell>
    </BaseListItem>
  );
};
OriginalListItem.displayName = 'OriginalListItem';

export default OriginalListItem;
